// src/features/live-shuttle/ui/live-shuttle-screen.tsx

import * as React from "react";
import { useParams } from "react-router-dom";
import {
  Card, CardHeader, CardTitle, CardContent
} from "@src/ui/views/card";
import {
  Table, TableHeader, TableRow, TableHead,
  TableBody, TableCell
} from "@src/ui/views/table";
import { Avatar, AvatarImage, AvatarFallback } from "@src/ui/views/avatar";
import { Badge } from "@src/ui/views/badge";
import {
  Dialog, DialogTrigger, DialogContent, DialogTitle
} from "@src/ui/views/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@src/ui/views/select";
import { Clock, Calendar as CalendarIcon } from "lucide-react";

import {
  useGetLiveShuttleSummaryQuery,
  useGetRecentLiveShuttlesQuery,
} from "../../redux/live-shuttle-api";
import type {
  LiveShuttleSummary,
  RecentLiveShuttle
} from "../../live-shuttle-models";

export function LiveShuttleScreen() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const [limit,      setLimit]      = React.useState(50);
  const [selectedDate, setSelectedDate] = React.useState(
    new Date().toISOString().slice(0,10)
  );

  // queries auto‐refetch when their args change
  const { data: sumRes,   isLoading: sumLoading } = useGetLiveShuttleSummaryQuery({
    spaceId: spaceId!,
    date:    selectedDate
  });
  const { data: recRes,   isLoading: recLoading } = useGetRecentLiveShuttlesQuery({
    spaceId: spaceId!,
    date:    selectedDate,
    limit
  });

  if (sumLoading || recLoading) return <p>Loading…</p>;
  if (!sumRes || !recRes)       return <p>Error loading data</p>;

  const summary = sumRes.data as LiveShuttleSummary;
  const recent  = recRes.data as RecentLiveShuttle[];

  // formatting helpers
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, { hour:"2-digit", minute:"2-digit" });

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow">
      {/* date picker + limit selector */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Dialog>
          <DialogTrigger asChild>
            <button className="inline-flex items-center px-3 py-1 border rounded">
              <CalendarIcon className="mr-1"/> {selectedDate}
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-xs">
            <DialogTitle>Select Date</DialogTitle>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="mt-2 w-full border rounded px-2 py-1"
            />
          </DialogContent>
        </Dialog>

        <label className="flex items-center space-x-2 text-sm">
          <span>Show last</span>
          <Select value={String(limit)} onValueChange={v => setLimit(Number(v))}>
            <SelectTrigger className="w-18">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {[5,10,20,50, 100, 200].map(n => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span>requests</span>
        </label>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Total Orders</CardTitle>
            <Clock className="h-5 w-5 text-muted-foreground"/>
          </CardHeader>
          <CardContent className="text-3xl font-bold">
            {summary.total_orders}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Avg Accept Time</CardTitle>
            <Clock className="h-5 w-5 text-muted-foreground"/>
          </CardHeader>
          <CardContent className="text-3xl font-bold">
            {Math.round(summary.avg_accept_seconds)} sec
          </CardContent>
        </Card>
      </div>

      {/* Table of that day’s requests */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Requester</TableHead>
            <TableHead>Requested</TableHead>
            <TableHead>Accepted By</TableHead>
            <TableHead>Wait</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {recent.map(r => (
            <TableRow key={r.rideId} className="hover:bg-gray-50">
              <TableCell>
                <div className="flex items-center space-x-2">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={r.requesterProfilePic||""} alt={r.requesterName}/>
                    <AvatarFallback>{r.requesterName.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <span>{r.requesterName}</span>
                </div>
              </TableCell>
              <TableCell>
                {fmtTime(r.requestTime)}
              </TableCell>
              <TableCell>
                {r.driverName ? (
                  <div className="flex items-center space-x-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={r.driverProfilePic||""} alt={r.driverName}/>
                      <AvatarFallback>{r.driverName.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span>{r.driverName}</span>
                  </div>
                ) : "—"}
              </TableCell>
              <TableCell>
                {r.timeToAcceptSeconds != null
                  ? `${r.timeToAcceptSeconds.toFixed(1)} sec`
                  : "—"}
              </TableCell>

            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
